/**
 * cg-mitm/1 — Ed25519 trust-root verify, purpose/KDF/AAD helpers, server-cert issue.
 *
 * Uses Web Crypto Ed25519 (Node ≥22 / modern browsers) so this stays in the
 * same export graph as the rest of `@cursor-gateway/e2ee` (no node:crypto).
 */
import { CG_MITM_HPKE_SUITE, CG_MITM_PROTOCOL, CG_MITM_PURPOSE_C2S, CG_MITM_PURPOSE_ENROLL, CG_MITM_PURPOSE_S2C, cgDeviceCertSchema, cgServerIdentityCertSchema, cgTrustRootPublicSchema } from "@cursor-gateway/shared";
import { canonicalBytes, decodeBase64Url, encodeBase64Url, exportPrivateJwk, importSigningPublicKey, signValue, verifyValue } from "./index.js";
function omitSignature(value) {
    const { signature: _signature, ...rest } = value;
    return rest;
}
const subtle = globalThis.crypto.subtle;
function toArrayBuffer(value) {
    const output = new Uint8Array(value.byteLength);
    output.set(value);
    return output.buffer;
}
async function sha256(value) {
    return new Uint8Array(await subtle.digest("SHA-256", toArrayBuffer(value)));
}
// Re-export purpose constants for callers that import from e2ee.
export { CG_MITM_PROTOCOL, CG_MITM_HPKE_SUITE, CG_MITM_PURPOSE_C2S, CG_MITM_PURPOSE_S2C, CG_MITM_PURPOSE_ENROLL };
/** Alias matching the 04-handshake-kdf-aad.md names. */
export const C2S_PURPOSE = CG_MITM_PURPOSE_C2S;
export const S2C_PURPOSE = CG_MITM_PURPOSE_S2C;
export const ENROLL_PURPOSE = CG_MITM_PURPOSE_ENROLL;
export function buildHandshakeContext(v) {
    return {
        protocol: CG_MITM_PROTOCOL,
        purpose: "handshake",
        serverCertId: v.serverCertId,
        epoch: v.epoch,
        deviceId: v.deviceId,
        adapterNonce: v.adapterNonce,
        minSuite: v.minSuite
    };
}
export function buildC2sAad(env) {
    return {
        protocol: CG_MITM_PROTOCOL,
        direction: "c2s",
        kind: env.kind,
        sessionId: env.sessionId,
        sequence: env.sequence
    };
}
export function buildS2cAad(v) {
    return {
        protocol: CG_MITM_PROTOCOL,
        direction: "s2c",
        sessionId: v.sessionId,
        sequence: v.sequence,
        frameType: v.frameType
    };
}
/**
 * Canonical transcript the Adapter's device signing key signs on every exchange
 * (first and subsequent frames). The server verifies it with the enrolled device
 * cert's ES256 public key. Long-term keys never enter an HTTP header — the
 * signature travels inside the encrypted `cgExchangeInner.deviceAuth`.
 */
export function buildCgDeviceAuthTranscript(v) {
    return {
        protocol: CG_MITM_PROTOCOL,
        purpose: "device-auth",
        sessionId: v.sessionId,
        deviceId: v.deviceId,
        sequence: v.sequence,
        idempotencyKey: v.idempotencyKey
    };
}
export function buildEnrollContext(env) {
    return {
        protocol: CG_MITM_PROTOCOL,
        purpose: "enroll-handshake",
        serverCertId: env.serverCertId,
        epoch: env.epoch
    };
}
export function buildEnrollAad(env) {
    return {
        protocol: CG_MITM_PROTOCOL,
        direction: "enroll",
        serverCertId: env.serverCertId,
        epoch: env.epoch
    };
}
export function cgServerCertTranscript(cert) {
    return {
        protocol: cert.protocol,
        kind: cert.kind,
        version: cert.version,
        certId: cert.certId,
        serverId: cert.serverId,
        epoch: cert.epoch,
        hpkeFingerprint: cert.hpkeKey.fingerprint,
        hpkeKeyId: cert.hpkeKey.keyId,
        signingFingerprint: cert.signingKey.fingerprint,
        signingKeyId: cert.signingKey.keyId,
        allowedOrigins: [...cert.allowedOrigins].sort(),
        issuedAt: cert.issuedAt,
        expiresAt: cert.expiresAt,
        rootKeyId: cert.rootKeyId,
        rootFingerprint: cert.rootFingerprint,
        alg: cert.alg
    };
}
export function cgDeviceCertTranscript(cert) {
    if (cert.kind === "cg-device-cert/2") {
        const v2 = cert;
        return {
            protocol: v2.protocol,
            kind: v2.kind,
            version: v2.version,
            accountId: v2.accountId,
            deviceId: v2.deviceId,
            epoch: v2.epoch,
            signingFingerprint: v2.signingKey.fingerprint,
            signingKeyId: v2.signingKey.keyId,
            encryptionFingerprint: v2.encryptionKey.fingerprint,
            encryptionKeyId: v2.encryptionKey.keyId,
            keyIdHint: v2.keyIdHint,
            authScope: v2.authScope,
            issuedAt: v2.issuedAt,
            expiresAt: v2.expiresAt,
            serverCertId: v2.serverCertId
        };
    }
    const v1 = cert;
    return {
        protocol: v1.protocol,
        kind: v1.kind,
        version: v1.version,
        deviceId: v1.deviceId,
        signingFingerprint: v1.signingKey.fingerprint,
        signingKeyId: v1.signingKey.keyId,
        encryptionFingerprint: v1.encryptionKey.fingerprint,
        encryptionKeyId: v1.encryptionKey.keyId,
        keyIdHint: v1.keyIdHint,
        issuedAt: v1.issuedAt,
        expiresAt: v1.expiresAt,
        serverCertId: v1.serverCertId
    };
}
export async function generateCgEd25519KeyPair() {
    return subtle.generateKey("Ed25519", true, ["sign", "verify"]);
}
export async function exportCgEd25519PublicKey(key) {
    const jwk = await subtle.exportKey("jwk", key);
    if (jwk.kty !== "OKP" || jwk.crv !== "Ed25519" || typeof jwk.x !== "string") {
        throw new Error("invalid_ed25519_public_key");
    }
    return { kty: "OKP", crv: "Ed25519", x: jwk.x };
}
export async function importCgEd25519PublicKey(value) {
    return subtle.importKey("jwk", { ...value, ext: true, key_ops: ["verify"] }, "Ed25519", true, ["verify"]);
}
export async function importCgEd25519PrivateKey(jwk) {
    return subtle.importKey("jwk", { ...jwk, ext: true, key_ops: ["sign"] }, "Ed25519", false, ["sign"]);
}
export async function createCgEd25519KeyDescriptor(key) {
    const exported = await exportCgEd25519PublicKey(key);
    const digest = encodeBase64Url(await sha256(canonicalBytes(exported)));
    return {
        keyId: `ed25519-${digest.slice(0, 22)}`,
        fingerprint: `sha256:${digest}`,
        publicKey: exported
    };
}
export async function generateCgTrustRootKeyPair(epoch = 1) {
    const pair = await generateCgEd25519KeyPair();
    const descriptor = await createCgEd25519KeyDescriptor(pair.publicKey);
    const privateJwk = await exportPrivateJwk(pair.privateKey);
    return {
        privateJwk,
        privateKey: pair.privateKey,
        public: cgTrustRootPublicSchema.parse({
            protocol: CG_MITM_PROTOCOL,
            kind: "cg-trust-root-public/1",
            alg: "EdDSA",
            keyId: descriptor.keyId,
            fingerprint: descriptor.fingerprint,
            publicKey: descriptor.publicKey,
            epoch,
            createdAt: new Date().toISOString()
        })
    };
}
export async function signCgEdDsa(value, privateKey, keyId) {
    const signature = new Uint8Array(await subtle.sign("Ed25519", privateKey, toArrayBuffer(canonicalBytes(value))));
    return {
        alg: "EdDSA",
        keyId,
        value: encodeBase64Url(signature)
    };
}
/**
 * alg discriminant: EdDSA → Web Crypto Ed25519; ES256 → existing verifyValue.
 */
export async function verifyCgSignature(value, sig, pub) {
    if (sig.alg === "ES256") {
        return verifyValue(value, sig, pub);
    }
    try {
        return subtle.verify("Ed25519", pub, toArrayBuffer(decodeBase64Url(sig.value)), toArrayBuffer(canonicalBytes(value)));
    }
    catch {
        return false;
    }
}
export async function issueCgServerIdentityCert(input) {
    if (input.rootPublic.alg !== "EdDSA") {
        throw new Error("cg_server_cert_requires_eddsa_root");
    }
    const issuedAt = input.issuedAt ?? new Date().toISOString();
    const validityDays = input.validityDays ?? 365;
    const expiresAt = new Date(Date.parse(issuedAt) + validityDays * 86_400_000).toISOString();
    const unsigned = {
        protocol: CG_MITM_PROTOCOL,
        kind: "cg-server-identity-cert/1",
        version: 1,
        certId: crypto.randomUUID(),
        serverId: input.serverId,
        epoch: input.epoch ?? input.rootPublic.epoch,
        hpkeKey: input.hpkeKey,
        signingKey: input.signingKey,
        allowedOrigins: input.allowedOrigins,
        issuedAt,
        expiresAt,
        rootKeyId: input.rootPublic.keyId,
        rootFingerprint: input.rootPublic.fingerprint,
        alg: "EdDSA"
    };
    const signature = await signCgEdDsa(cgServerCertTranscript(unsigned), input.rootPrivateKey, input.rootPublic.keyId);
    return cgServerIdentityCertSchema.parse({ ...unsigned, signature });
}
export async function verifyCgServerIdentityCert(input) {
    const cert = input.cert;
    const now = input.nowMs ?? Date.now();
    if (cert.protocol !== CG_MITM_PROTOCOL || cert.kind !== "cg-server-identity-cert/1") {
        return { ok: false, reason: "cert_kind_mismatch" };
    }
    if (cert.version !== 1)
        return { ok: false, reason: "cert_version_unsupported" };
    if (Date.parse(cert.expiresAt) <= now)
        return { ok: false, reason: "cert_expired" };
    if (Date.parse(cert.issuedAt) > now + 60_000)
        return { ok: false, reason: "cert_not_yet_valid" };
    if (input.expected?.serverId && cert.serverId !== input.expected.serverId) {
        return { ok: false, reason: "cert_server_mismatch" };
    }
    if (input.expected?.epoch !== undefined && cert.epoch !== input.expected.epoch) {
        return { ok: false, reason: "cert_epoch_mismatch" };
    }
    if (input.expected?.hpkeFingerprint &&
        cert.hpkeKey.fingerprint !== input.expected.hpkeFingerprint) {
        return { ok: false, reason: "cert_hpke_fingerprint_mismatch" };
    }
    if (input.expected?.signingFingerprint &&
        cert.signingKey.fingerprint !== input.expected.signingFingerprint) {
        return { ok: false, reason: "cert_signing_fingerprint_mismatch" };
    }
    if (input.expected?.origin && !cert.allowedOrigins.includes(input.expected.origin)) {
        return { ok: false, reason: "cert_origin_not_allowed" };
    }
    const root = input.trustRoots.find((candidate) => candidate.keyId === cert.rootKeyId &&
        candidate.fingerprint === cert.rootFingerprint);
    if (!root)
        return { ok: false, reason: "trust_root_not_found" };
    if (root.alg !== "EdDSA" || root.publicKey.kty !== "OKP") {
        return { ok: false, reason: "trust_root_alg_mismatch" };
    }
    if (cert.signature.alg !== "EdDSA")
        return { ok: false, reason: "cert_signature_alg_mismatch" };
    if (cert.signature.keyId !== root.keyId) {
        return { ok: false, reason: "cert_signature_key_mismatch" };
    }
    const rootKey = await importCgEd25519PublicKey(root.publicKey);
    const valid = await verifyCgSignature(cgServerCertTranscript(omitSignature(cert)), cert.signature, rootKey);
    if (!valid)
        return { ok: false, reason: "cert_signature_invalid" };
    return { ok: true, root };
}
export async function issueCgDeviceCert(input) {
    const issuedAt = input.issuedAt ?? new Date().toISOString();
    const validityDays = input.validityDays ?? 365;
    const expiresAt = new Date(Date.parse(issuedAt) + validityDays * 86_400_000).toISOString();
    const unsigned = {
        protocol: CG_MITM_PROTOCOL,
        kind: "cg-device-cert/1",
        version: 1,
        deviceId: input.deviceId,
        signingKey: input.signingKey,
        encryptionKey: input.encryptionKey,
        keyIdHint: input.keyIdHint,
        issuedAt,
        expiresAt,
        serverCertId: input.serverCertId
    };
    const signature = await signValue(cgDeviceCertTranscript(unsigned), input.signingPrivateKey, input.signingKeyId);
    return cgDeviceCertSchema.parse({ ...unsigned, signature });
}
/** Issue account-bound device cert (cg-device-cert/2). */
export async function issueCgDeviceCertV2(input) {
    const issuedAt = input.issuedAt ?? new Date().toISOString();
    const validityDays = input.validityDays ?? 365;
    const expiresAt = new Date(Date.parse(issuedAt) + validityDays * 86_400_000).toISOString();
    const unsigned = {
        protocol: CG_MITM_PROTOCOL,
        kind: "cg-device-cert/2",
        version: 2,
        accountId: input.accountId,
        deviceId: input.deviceId,
        epoch: input.epoch ?? 1,
        signingKey: input.signingKey,
        encryptionKey: input.encryptionKey,
        keyIdHint: input.keyIdHint,
        authScope: input.authScope ?? "api-key",
        issuedAt,
        expiresAt,
        serverCertId: input.serverCertId
    };
    const signature = await signValue(cgDeviceCertTranscript(unsigned), input.signingPrivateKey, input.signingKeyId);
    return cgDeviceCertSchema.parse({ ...unsigned, signature });
}
export async function verifyCgDeviceCert(input) {
    if (input.cert.signature.keyId.length < 8)
        return false;
    const pub = "kty" in input.serverSigningPublicKey
        ? await importSigningPublicKey(input.serverSigningPublicKey)
        : input.serverSigningPublicKey;
    return verifyValue(cgDeviceCertTranscript(omitSignature(input.cert)), input.cert.signature, pub);
}
/** sessionId = base64url(SHA-256(decodeBase64Url(enc.enc))). */
export async function sessionIdFromHpkeEnc(encBase64Url) {
    return encodeBase64Url(await sha256(decodeBase64Url(encBase64Url)));
}
//# sourceMappingURL=cgMitm.js.map